import { FUNNEL_EVENT_PROPERTY_KEYS, type OnfidoStepCompletedProperties } from 'app/tracking/events'

/**
 * Explicitly approved Onfido SDK property keys for PostHog.
 * Default-deny: anything not listed here is discarded (including PII and unknown fields).
 * Matching is case-insensitive; emitted keys are always lowercase.
 */
const ONFIDO_PROPERTY_ALLOWLIST = new Set(['step_name', 'step', 'screen', 'page', 'document_type', 'country', 'is_cross_device', 'error_code'])

function pickString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
        return value.trim()
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value)
    }
    return undefined
}

/** Unwrap `{ properties: ... }` envelopes emitted by some Onfido SDK callers. */
export function normalizeOnfidoEventProperties(raw: Record<string, unknown> | undefined): Record<string, unknown> {
    if (!raw) {
        return {}
    }

    const nested = raw.properties
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        return nested as Record<string, unknown>
    }

    return raw
}

/** Keep only allowlisted, flat fields for the legacy Onfido Event payload. */
export function sanitizeOnfidoLegacyProperties(raw: Record<string, unknown> | undefined): Record<string, string> {
    const normalized = normalizeOnfidoEventProperties(raw)
    const sanitized: Record<string, string> = {}

    for (const [key, value] of Object.entries(normalized)) {
        const canonicalKey = key.toLowerCase()
        if (!ONFIDO_PROPERTY_ALLOWLIST.has(canonicalKey)) {
            continue
        }
        const asString = pickString(value)
        if (asString !== undefined) {
            sanitized[canonicalKey] = asString
        }
    }

    return sanitized
}

export function buildOnfidoStepCompletedProperties(
    applicationId: string,
    eventName: string,
    rawProperties: Record<string, unknown> | undefined
): OnfidoStepCompletedProperties {
    const sanitized = sanitizeOnfidoLegacyProperties(rawProperties)
    const step_name = sanitized.step_name ?? sanitized.step ?? sanitized.screen ?? sanitized.page ?? 'unknown'

    return {
        [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: applicationId,
        [FUNNEL_EVENT_PROPERTY_KEYS.EVENT_TYPE]: eventName || 'unknown',
        [FUNNEL_EVENT_PROPERTY_KEYS.STEP_NAME]: step_name,
        ...(sanitized.error_code ? { [FUNNEL_EVENT_PROPERTY_KEYS.ERROR_CODE]: sanitized.error_code } : {})
    }
}
